import { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, FileImage, Zap, BookmarkCheck, Truck, Store, Handshake, UserPlus, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../lib/api";
import { TAX_RATES, promoDiscount, itemUnitPrice } from "../lib/pricing";
import { trackInitiateCheckout } from "../lib/pixels";
import { itemName, itemSizeLabel } from "../lib/localize";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { AutoPromoBienvenue } from "../components/AutoPromoBienvenue";
import { useLang } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { GoogleRatingBadge } from "../components/GoogleRatingBadge";
import { PromoCodeInput } from "../components/PromoCodeInput";
import FreeShippingBar from "../components/FreeShippingBar";
import DatePicker from "../components/DatePicker";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)

const FIELDS = [
  { name: "last_name", required: true },
  { name: "first_name", required: true },
  { name: "company", required: false },
  { name: "address1", required: true },
  { name: "address2", required: false },
  { name: "city", required: true },
  { name: "postal_code", required: true },
  { name: "phone", required: true, type: "tel" },
  { name: "email", required: true, type: "email" },
];

export default function Checkout() {
  const { items, promo, applyPromo, clearPromo, clear } = useCart();
  const { user } = useAuth();
  const { lang, t, ts } = useLang();
  const [form, setForm] = useState({ last_name: "", first_name: "", company: "", address1: "", address2: "", city: "", province: "QC", postal_code: "", phone: "", email: "", delivery_date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [express, setExpress] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState("livraison");
  const [settings, setSettings] = useState({ shipping_standard: 14.95, shipping_min: 17.95, shipping_free_over: 250, express_rate: 0.15, shop_open: true, hand_delivery_fee: 15, hand_delivery_radius_km: 25 });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [shortcutId, setShortcutId] = useState(null);
  const [handInfo, setHandInfo] = useState(null);  // éligibilité livraison en main propre
  const prefilledRef = useRef(false);

  // Livraison en main propre : vérifier l'éligibilité dès que le code postal est complet
  useEffect(() => {
    const code = (form.postal_code || "").replace(/\s/g, "");
    if (!/^[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d$/.test(code)) { setHandInfo(null); return; }
    const timer = setTimeout(() => {
      api.get("/delivery/hand", { params: { postal_code: code } })
        .then((r) => setHandInfo(r.data))
        .catch(() => setHandInfo(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [form.postal_code]);

  // Si le code postal devient inéligible, retomber sur la livraison standard
  useEffect(() => {
    if (handInfo && !handInfo.eligible && deliveryMethod === "main_propre") setDeliveryMethod("livraison");
  }, [handInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/settings/shop").then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  useEffect(() => {
    // Pré-remplir une seule fois, sans jamais écraser un champ déjà saisi.
    // Priorité : fiche d'inscription (nom, téléphone, adresse complète), puis
    // carnet d'adresses (raccourcis cliquables par-dessus).
    if (user && !prefilledRef.current) {
      prefilledRef.current = true;
      const parts = (user.name || "").trim().split(/\s+/);
      setForm((f) => ({
        ...f,
        first_name: f.first_name || user.first_name || parts[0] || "",
        last_name: f.last_name || user.last_name || parts.slice(1).join(" ") || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
        address1: f.address1 || user.address1 || "",
        city: f.city || user.city || "",
        province: user.province || f.province,
        postal_code: f.postal_code || user.postal_code || "",
      }));
      api.get("/account/profile").then((res) => {
        setSavedAddresses(res.data.addresses || []);
        setForm((f) => ({ ...f, phone: f.phone || res.data.phone || "" }));
      }).catch(() => {});
    }
  }, [user]);

  const applyShortcut = (a) => {
    setForm((f) => ({
      ...f,
      address1: a.address1, address2: a.address2 || "",
      city: a.city, postal_code: a.postal_code,
      phone: a.phone || f.phone,
    }));
    setShortcutId(a.id);
  };

  // Inscription OBLIGATOIRE pour acheter : au lieu d'une redirection silencieuse,
  // on explique clairement et on propose d'abord la création de compte.
  // Le panier (localStorage) est intact au retour (/commande).
  if (user === false) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6">
        <SEO title={ts("checkout.gate_title")} path="/commande" noindex />
        <div className="max-w-md w-full bg-surface border border-gold/25 rounded-2xl p-8 text-center" data-testid="checkout-auth-gate">
          <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mx-auto mb-5">
            <UserPlus className="w-7 h-7 text-gold" />
          </div>
          <h1 className="font-serif text-2xl text-ivory mb-3" data-testid="checkout-gate-title">{t("checkout.gate_title")}</h1>
          <p className="text-sm text-ivory/75 mb-7" data-testid="checkout-gate-text">{t("checkout.gate_text")}</p>
          <Link to="/inscription" state={{ next: "/commande" }} data-testid="checkout-gate-register"
            className="block w-full bg-gold text-ink font-semibold rounded-lg px-5 py-3.5 hover:bg-champagne transition-colors duration-300 mb-3">
            {t("checkout.gate_register")}
          </Link>
          <Link to="/connexion" state={{ next: "/commande" }} data-testid="checkout-gate-login"
            className="block w-full border border-ivory/25 text-ivory/85 rounded-lg px-5 py-3 text-sm hover:border-gold hover:text-gold transition-colors duration-300">
            {t("checkout.gate_login")}
          </Link>
        </div>
      </div>
    );
  }
  // Soumission envoyée : écran de confirmation AVANT le test « panier vide »
  // (le panier vient d'être vidé par l'envoi)
  if (sent) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6 pt-24 pb-16">
        <SEO title={ts("checkout.sent_title")} path="/commande" noindex />
        <div className="max-w-lg w-full bg-surface border border-gold/25 rounded-2xl p-8 text-center" data-testid="checkout-quote-sent">
          <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-7 h-7 text-gold" />
          </div>
          <h1 className="font-serif text-3xl text-ivory mb-4" data-testid="checkout-sent-title">{t("checkout.sent_title")}</h1>
          <p className="text-sm text-ivory/75 mb-8 leading-relaxed" data-testid="checkout-sent-text">{t("checkout.sent_text")}</p>
          <Link to="/compte" data-testid="checkout-sent-account"
            className="block w-full bg-gold text-ink font-semibold rounded-full px-5 py-3.5 hover:bg-champagne transition-colors duration-300 mb-3">
            {t("nav.account")}
          </Link>
          <Link to="/boutique" data-testid="checkout-sent-shop"
            className="block w-full border border-ivory/25 text-ivory/85 rounded-full px-5 py-3 text-sm hover:border-gold hover:text-gold transition-colors duration-300">
            {t("nav.catalogue")}
          </Link>
        </div>
      </div>
    );
  }
  if (user && items.length === 0) return <Navigate to="/panier" replace />;

  const subtotal = items.reduce((s, i) => s + itemUnitPrice(i) * i.qty, 0);
  const discount = promoDiscount(items, promo);
  const expressFee = express ? Math.round(subtotal * settings.express_rate * 100) / 100 : 0;
  const shippingFee = deliveryMethod === "ramassage" ? 0
    : deliveryMethod === "main_propre"
      ? (subtotal >= settings.shipping_free_over ? 0 : Math.round((handInfo?.fee ?? 15) * 100) / 100)
      : (subtotal >= settings.shipping_free_over ? 0 : Math.round(Math.max(settings.shipping_standard, settings.shipping_min || 0) * 100) / 100);
  const taxable = Math.round((subtotal - discount + shippingFee + expressFee) * 100) / 100;
  const rates = TAX_RATES[form.province] || { gst: 0.05 };
  const tps = Math.round(taxable * (rates.gst || 0) * 100) / 100;
  const tvq = Math.round(taxable * (rates.qst || 0) * 100) / 100;
  const tvh = Math.round(taxable * (rates.hst || 0) * 100) / 100;
  const total = Math.round((taxable + tps + tvq + tvh) * 100) / 100;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value }));

  const deliveryOptions = [
    { id: "livraison", label: t("checkout.delivery_ship"), desc: t("checkout.delivery_ship_desc"), Icon: Truck },
    { id: "ramassage", label: t("checkout.delivery_pickup"), desc: t("checkout.delivery_pickup_desc"), Icon: Store },
    ...(handInfo?.eligible ? [{
      id: "main_propre", label: t("checkout.delivery_hand"), Icon: Handshake,
      desc: `${ts("checkout.delivery_hand_desc", { km: handInfo.radius_km || 25 })} — ${subtotal >= settings.shipping_free_over ? t("checkout.shipping_free") : fmt(handInfo.fee ?? settings.hand_delivery_fee)}`,
    }] : []),
  ];

  const buildPayload = () => ({
    customer: form,
    items: items.map((i) => ({
      product_id: i.product_id, size_id: i.size_id, qty: i.qty, finish_id: i.finish_id || "",
      inscription_type: i.inscription_type, inscription_text: i.inscription_text,
      logo_path: i.logo_path || "", logo_name: i.logo_name || "",
    })),
    origin_url: window.location.origin,
    lang,
    express,
    delivery_method: deliveryMethod,
    promo_code: promo?.code || "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      trackInitiateCheckout(total);
      // Décision du propriétaire (2026-10-09) : TOUT achat passe par une soumission —
      // le panier part prérempli à l'atelier, le paiement se fait après acceptation.
      await api.post("/orders/as-quote", buildPayload());
      clear();
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-28">
      <SEO title={ts("seo.home_title")} description={ts("seo.home_desc")} path="/commande" noindex />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("checkout.kicker")}</p>
        <h1 className="font-serif text-5xl md:text-6xl mb-6">{t("checkout.title")}</h1>
        <div className="mb-12"><GoogleRatingBadge /></div>
      </FadeUp>

      {user && user.email_verified === false && (
        <div data-testid="checkout-unverified" className="mb-8 border border-gold/30 bg-gold/10 rounded-xl p-5 text-sm text-gold">
          {t("verify.banner")}
        </div>
      )}

      <form onSubmit={submit} className="grid lg:grid-cols-5 gap-12">
        <FadeUp delay={0.05} className="lg:col-span-3 space-y-5">
          {savedAddresses.length > 0 && (
            <div data-testid="saved-addresses" className="border border-gold/20 rounded-xl p-4">
              <p className="text-xs text-ivory/60 mb-3 uppercase tracking-widest">{t("checkout.saved_addresses")}</p>
              <div className="flex flex-wrap gap-2">
                {savedAddresses.map((a) => (
                  <button
                    key={a.id} type="button" data-testid={`shortcut-${a.id}`}
                    onClick={() => applyShortcut(a)}
                    className={`inline-flex items-center gap-2 text-xs px-4 py-2 rounded-full border transition-colors duration-300 ${
                      shortcutId === a.id ? "border-gold bg-gold/15 text-gold" : "border-ivory/20 text-ivory/70 hover:border-gold hover:text-gold"
                    }`}
                  >
                    {shortcutId === a.id && <BookmarkCheck className="w-3.5 h-3.5" data-testid="shortcut-icon" />}
                    {a.label || `${a.address1}, ${a.city}`}
                  </button>
                ))}
              </div>
              {shortcutId && (
                <p data-testid="shortcut-active" className="inline-flex items-center gap-1.5 text-xs text-gold mt-3">
                  <BookmarkCheck className="w-4 h-4" /> {t("checkout.shortcut_active")}
                </p>
              )}
            </div>
          )}
          {FIELDS.map((f) => (
            <div key={f.name}>
              <label className="block text-sm text-ivory/70 mb-2">{t(`checkout.${f.name}`)}{f.required && <span className="text-gold"> *</span>}</label>
              <input
                data-testid={`checkout-${f.name}`}
                type={f.type || "text"}
                value={form[f.name]} onChange={set(f.name)} required={f.required}
                className="w-full bg-surface border border-ivory/15 rounded-lg px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300"
              />
            </div>
          ))}
          <button
            type="button"
            data-testid="express-toggle"
            onClick={() => setExpress((v) => !v)}
            className={`w-full text-left flex items-center gap-4 rounded-xl border p-5 transition-colors duration-300 ${
              express ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/50"
            }`}
          >
            <Zap className={`w-6 h-6 shrink-0 ${express ? "text-gold" : "text-ivory/65"}`} />
            <span className="flex-1">
              <span className="block font-semibold text-sm">{t("express.title")}</span>
              <span className="block text-xs text-ivory/70 mt-1">{t("express.desc")}</span>
            </span>
            <span className="text-sm text-champagne font-semibold whitespace-nowrap">{fmt(Math.round(items.reduce((s, i) => s + itemUnitPrice(i) * i.qty, 0) * settings.express_rate * 100) / 100)}</span>
          </button>
          <div>
            <label className="block text-sm text-ivory/70 mb-2">{t("checkout.delivery_method")} <span className="text-gold">*</span></label>
            <div className={`grid gap-3 ${deliveryOptions.length > 2 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2"}`} data-testid="delivery-method-selector">
              {deliveryOptions.map((m) => (
                <button
                  type="button" key={m.id} data-testid={`delivery-${m.id}`}
                  onClick={() => setDeliveryMethod(m.id)}
                  className={`flex flex-col items-start gap-1 border rounded-xl px-4 py-3.5 transition-colors duration-300 ${deliveryMethod === m.id ? "border-gold bg-gold/10 text-gold" : "border-ivory/15 text-ivory/70 hover:border-gold/40"}`}
                >
                  <span className="inline-flex items-center gap-2 text-sm"><m.Icon className="w-4 h-4" /> {m.label}</span>
                  <span className="text-[11px] text-ivory/70 text-left">{m.desc}</span>
                </button>
              ))}
            </div>
            {deliveryMethod === "ramassage" && (
              <p data-testid="pickup-note" className="text-xs text-gold mt-2">{t("checkout.pickup_note")}</p>
            )}
            {deliveryMethod === "main_propre" && (
              <div data-testid="hand-note" className="mt-2">
                <p className="text-xs text-gold mb-2">{t("checkout.hand_note")}</p>
                {/* Grille des frais par distance — le palier applicable est souligné */}
                <div data-testid="hand-fee-tiers" className="rounded-lg border border-ivory/10 divide-y divide-ivory/10 text-xs">
                  {(handInfo?.tiers || []).map((tr, i, arr) => {
                    const from = i === 0 ? 0 : arr[i - 1].max_km;
                    const active = handInfo.fee === tr.fee;
                    return (
                      <div key={tr.max_km} data-testid={`hand-tier-${tr.max_km}`}
                        className={`flex justify-between px-3 py-2 ${active ? "bg-gold/10 text-gold font-semibold" : "text-ivory/60"}`}>
                        <span>{i === 0
                          ? t("checkout.hand_tier_under", { km: tr.max_km })
                          : t("checkout.hand_tier_range", { from, to: tr.max_km })}</span>
                        <span>{fmt(tr.fee)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {deliveryMethod === "livraison" && (
              <p data-testid="ship-beyond-note" className="text-xs text-ivory/55 mt-2">{t("checkout.beyond_note")}</p>
            )}
          </div>
          <div>
            <label className="block text-sm text-ivory/70 mb-2">{t("checkout.province")} <span className="text-gold">*</span></label>
            <select
              data-testid="checkout-province"
              value={form.province} onChange={set("province")} required
              className="w-full bg-surface border border-ivory/15 rounded-lg px-4 py-3.5 text-sm text-ivory focus:border-gold focus:outline-none transition-colors duration-300"
            >
              {Object.entries(t("checkout.provinces")).map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-ivory/70 mb-2">{t("checkout.delivery_label")} <span className="text-gold">*</span></label>
            <DatePicker
              testid="checkout-delivery-date"
              value={form.delivery_date}
              onChange={(v) => setForm((f) => ({ ...f, delivery_date: v }))}
              minDate={new Date(Date.now() + 1 * 86400000)}
              placeholder={ts("checkout.delivery_label")}
            />
            <p className="text-xs text-ivory/65 mt-2">{t("checkout.delivery_note")}</p>
          </div>
          <div>
            <label className="block text-sm text-ivory/70 mb-2">{t("checkout.notes_label")}</label>
            <textarea
              data-testid="checkout-notes" rows={3} value={form.notes} onChange={set("notes")}
              placeholder={ts("checkout.notes_placeholder")}
              className="w-full bg-surface border border-ivory/15 rounded-lg px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/60 focus:border-gold focus:outline-none transition-colors duration-300"
            />
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-2">
          <div className="bg-surface border border-gold/15 rounded-xl p-7 sticky top-24">
            <h3 className="font-serif text-2xl mb-6">{t("checkout.summary")}</h3>
            <div className="space-y-4 mb-6 max-h-64 overflow-y-auto pr-1">
              {items.map((i, idx) => (
                <div key={i.key} className="flex gap-3 text-sm" data-testid={`checkout-item-${idx}`}>
                  <img src={i.image} alt="" className="w-12 h-12 rounded object-contain bg-[#f4f2ec] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{itemName(i, lang)} <span className="text-ivory/65">× {i.qty}</span></p>
                    <p className="text-ivory/65 text-xs">{itemSizeLabel(i, lang)}</p>
                    {i.logo_name && (
                      <p className="text-ivory/65 text-xs flex items-center gap-1 mt-0.5">
                        <FileImage className="w-3 h-3 text-gold" /> {i.logo_name}
                      </p>
                    )}
                  </div>
                  <p className="text-champagne whitespace-nowrap">{fmt(itemUnitPrice(i) * i.qty)}</p>
                </div>
              ))}
            </div>
            <FreeShippingBar subtotal={Math.max(0, subtotal - discount)} />
            <div className="space-y-2 border-t border-ivory/10 pt-4 -mb-2 text-sm">
              <div className="flex justify-between text-ivory/70">
                <span>{t("checkout.subtotal")}</span>
                <span data-testid="checkout-subtotal">{fmt(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-gold">
                  <span>{t("promo.discount")} ({promo.code})</span>
                  <span data-testid="checkout-discount">−{fmt(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-ivory/70">
                <span>{{ ramassage: t("checkout.delivery_pickup"), main_propre: t("checkout.delivery_hand") }[deliveryMethod] || t("checkout.shipping")}</span>
                <span data-testid="checkout-shipping">{shippingFee === 0 ? t("checkout.shipping_free") : fmt(shippingFee)}</span>
              </div>
              {tps > 0 && (
                <div className="flex justify-between text-ivory/70">
                  <span>{t("checkout.tax_tps")}</span>
                  <span data-testid="checkout-tax-tps">{fmt(tps)}</span>
                </div>
              )}
              {tvq > 0 && (
                <div className="flex justify-between text-ivory/70">
                  <span>{t("checkout.tax_tvq")}</span>
                  <span data-testid="checkout-tax-tvq">{fmt(tvq)}</span>
                </div>
              )}
              {tvh > 0 && (
                <div className="flex justify-between text-ivory/70">
                  <span>{t("checkout.tax_tvh")}</span>
                  <span data-testid="checkout-tax-tvh">{fmt(tvh)}</span>
                </div>
              )}
            </div>
            {expressFee > 0 && (
              <div data-testid="checkout-express" className="flex justify-between text-sm text-gold border-t border-ivory/10 pt-4 -mb-2">
                <span className="inline-flex items-center gap-2"><Zap className="w-4 h-4" /> {t("express.fee", { pct: Math.round(settings.express_rate * 100) })}</span>
                <span data-testid="express-fee">{fmt(expressFee)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-gold/15 pt-4 mb-3">
              <span className="text-sm uppercase tracking-widest text-ivory/70">{t("checkout.total")}</span>
              <span data-testid="checkout-total" className="font-serif text-2xl text-gold">{fmt(total)}</span>
            </div>
            {/* Personnalisation non tarifée à la caisse (décision du propriétaire
                2026-10-09) : tout est confirmé dans la soumission */}
            <p className="text-[11px] text-gold/80 bg-gold/5 border border-gold/25 rounded-lg px-3 py-2.5 mb-6 leading-relaxed"
              data-testid="checkout-estimate-note">
              {t("checkout.estimate_note")}
            </p>
            {!settings.shop_open ? (
              <div data-testid="shop-closed-notice" className="border border-red-400/40 bg-red-400/10 text-red-300 rounded-lg px-4 py-4 text-sm text-center">
                {t("checkout.shop_closed")}
              </div>
            ) : (
            <>
            <div className="mb-5">
              <PromoCodeInput promo={promo} onApply={applyPromo} onClear={clearPromo} />
              <AutoPromoBienvenue />
            </div>
            {/* Tout achat passe par une soumission (décision du propriétaire) :
                aucun paiement à la caisse — le client paie après acceptation */}
            <button
              data-testid="checkout-quote-submit" type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 bg-gold text-ink font-semibold px-6 py-4 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              {loading ? t("checkout.paying") : t("checkout.pay")}
            </button>
            <p className="text-xs text-ivory/65 mt-4 text-center">{t("checkout.secure_note")}</p>
            <p className="text-xs text-ivory/70 mt-2 text-center" data-testid="checkout-returns-note">
              {t("checkout.returns_note")}{" "}
              <Link to="/retours" className="text-gold hover:text-champagne transition-colors duration-300 underline underline-offset-2">
                {t("returns.nav_link")}
              </Link>
            </p>
            </>
            )}
          </div>
        </FadeUp>
      </form>
    </div>
  );
}
